'use client';

import React, { useMemo } from 'react';

import { Section, Container } from '@layout';
import { Pagination, Heading, DEFAULT_PAGINATION_DATA } from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface PaginationDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  // optional data prop to drive the pager from real data
  data?: Array<{ id: number; name: string; note?: string }>;
  initialPageSize?: number;
  // optional render prop to customize how each row is rendered
  renderRow?: (item: { id: number; name: string; note?: string }, index: number) => React.ReactNode;
  // optional callbacks for server-driven pagination
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isSpanish?: boolean;
}

const PaginationDemo: React.FC<PaginationDemoProps> = ({
  isSpanish = false,
  sectionID = 'pagination',
  classes = '',
  containerId,
  containerClasses,
  data,
  initialPageSize,
  renderRow,
  onPageChange,
  onPageSizeChange,
}) => {

  const DATA = useMemo(() => {
    const base = data && data.length ? data : DEFAULT_PAGINATION_DATA;
    if (!isSpanish) return base;

    return base.map((item) => ({
      ...item,
      name: item.name.startsWith('Record #')
        ? `Registro #${item.name.replace('Record #', '')}`
        : item.name,
      note:
        item.note === 'Placeholder row for pagination demo.'
          ? 'Fila de ejemplo para la demo de paginación.'
          : item.note,
    }));
  }, [data, isSpanish]);

  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <Section sectionID={sectionID} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Pagination', 'Paginación')}</Heading>
        <p className='sub'>
          {t(
            'Reusable pager with first/prev/number/next/last, accessible keyboard shortcuts, and a page-size selector.',
            'Paginador reutilizable con primero/anterior/números/siguiente/último, atajos de teclado accesibles y selector de tamaño.'
          )}
        </p>

        <div id='pagiDemoHint' className='muted small'>
          {t(
            'Keyboard: ← → (prev/next), Home/End (first/last), PageUp/PageDown (skip pages). The demo uses a skip step of 5 pages.',
            'Teclado: ← → (anterior/siguiente), Inicio/Fin (primero/último), Av Pag/Re Pag (saltar paginas). La demo usa un salto de 5 paginas.'
          )}
        </div>

        <div aria-describedby='pagiDemoHint'>
          <Pagination
            id='pagiCard'
            items={DATA}
            defaultPageSize={initialPageSize ?? 8}
            step={5}
            onPageChange={onPageChange}
            onPageSizeChange={(e) => {
              const v = parseInt(e.target.value, 10) || (initialPageSize ?? 8);
              if (typeof onPageSizeChange === 'function') onPageSizeChange(v);
            }}
            renderRow={renderRow}
          />
        </div>
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="card pagination-card">
  <ul class="pagi-list">
    <li class="card">Row</li>
  </ul>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default PaginationDemo;
